import { useEffect, useState } from 'react';
import {
  Settings as SettingsIcon, MessageSquare, Plus, Edit2, Trash2, Globe, Building2, MapPin,
  X, Loader2, ToggleLeft, ToggleRight, ChevronUp, ChevronDown
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import type { SystemPrompt } from '../types';
import { PageHeader, fieldCls, chipCls } from '../../ui';

type Scope = 'global' | 'network' | 'site';

interface PromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (prompt: Partial<SystemPrompt>) => Promise<void>;
  prompt?: SystemPrompt | null;
  userRole: string;
  currentSiteId: string | null;
}

function PromptModal({ isOpen, onClose, onSave, prompt, userRole, currentSiteId }: PromptModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [promptText, setPromptText] = useState('');
  const [scope, setScope] = useState<Scope>('global');
  const [priority, setPriority] = useState(100);
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = userRole === 'super_admin';

  const availableScopes: { value: Scope; label: string; description: string }[] = [
    ...(isSuperAdmin ? [{ value: 'global' as Scope, label: 'Global', description: 'Applies to all users across all sites' }] : []),
    { value: 'network' as Scope, label: 'Network', description: 'Applies to all sites in the current network' },
    { value: 'site' as Scope, label: 'Site', description: 'Applies only to the current site' },
  ];

  useEffect(() => {
    if (isOpen && prompt) {
      setName(prompt.name);
      setDescription(prompt.description || '');
      setPromptText(prompt.prompt_text);
      setScope(prompt.scope);
      setPriority(prompt.priority);
    } else if (isOpen) {
      setName('');
      setDescription('');
      setPromptText('');
      setScope(isSuperAdmin ? 'global' : 'site');
      setPriority(100);
    }
  }, [isOpen, prompt, isSuperAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        id: prompt?.id,
        name,
        description: description || null,
        prompt_text: promptText,
        scope,
        priority,
        site_id: scope === 'site' ? currentSiteId : null,
      });
      onClose();
    } catch (error) {
      console.error('Error saving prompt:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col dark:bg-gray-800">
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {prompt ? 'Edit System Prompt' : 'Create System Prompt'}
          </h2>
          <button onClick={onClose} className="p-1 rounded text-gray-500 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., ICH GCP Guidelines Reference"
              className={`w-full ${fieldCls}`}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this prompt's purpose"
              className={`w-full ${fieldCls}`}
            />
          </div>

          {!prompt && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Scope</label>
              <div className="space-y-2">
                {availableScopes.map((s) => (
                  <label
                    key={s.value}
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      scope === s.value ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="scope"
                      value={s.value}
                      checked={scope === s.value}
                      onChange={(e) => setScope(e.target.value as Scope)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{s.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{s.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Priority
              <span className="font-normal text-gray-500 ml-1 dark:text-gray-400">(lower = higher priority)</span>
            </label>
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value) || 100)}
              min={1}
              max={1000}
              className={`w-32 ${fieldCls}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Prompt Text *</label>
            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="Enter the system prompt instructions..."
              rows={8}
              className={`w-full font-mono ${fieldCls}`}
              required
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              This text will be included in the AI's system prompt before answering questions.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 rounded-lg transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name || !promptText || saving}
              className="flex-1 px-4 py-2 bg-primary-600 text-sm font-medium text-white rounded-lg transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                prompt ? 'Save Changes' : 'Create Prompt'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface BasePrompt {
  name: string;
  description: string;
  prompt_text: string;
  is_readonly: boolean;
}

export function Settings() {
  const { currentSite, user } = useAuth();
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [basePrompt, setBasePrompt] = useState<BasePrompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<SystemPrompt | null>(null);
  const [filter, setFilter] = useState<'all' | Scope>('all');
  const [basePromptExpanded, setBasePromptExpanded] = useState(false);

  const userRole = user?.role || 'basic_user';
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = ['super_admin', 'administrator'].includes(userRole);

  useEffect(() => {
    if (isAdmin) {
      loadPrompts();
      if (isSuperAdmin) {
        loadBasePrompt();
      }
    } else {
      setLoading(false);
    }
  }, [currentSite, isSuperAdmin, isAdmin]);

  const loadPrompts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/intelligence/system-prompts?include_inactive=true');
      setPrompts(response.data || []);
    } catch (error) {
      console.error('Error loading system prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBasePrompt = async () => {
    try {
      const response = await api.get('/api/intelligence/base-prompt');
      setBasePrompt(response.data);
    } catch (error) {
      console.error('Error loading base prompt:', error);
    }
  };

  const handleSave = async (promptData: Partial<SystemPrompt>) => {
    if (promptData.id) {
      await api.patch(`/api/intelligence/system-prompts/${promptData.id}`, promptData);
    } else {
      await api.post('/api/intelligence/system-prompts', promptData);
    }
    await loadPrompts();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this system prompt?')) {
      return;
    }

    try {
      await api.delete(`/api/intelligence/system-prompts/${id}`);
      await loadPrompts();
    } catch (error: any) {
      console.error('Error deleting prompt:', error);
      alert(error.response?.data?.error || 'Failed to delete prompt. Please try again.');
    }
  };

  const handleToggleActive = async (prompt: SystemPrompt) => {
    try {
      await api.patch(`/api/intelligence/system-prompts/${prompt.id}`, {
        is_active: !prompt.is_active,
      });
      await loadPrompts();
    } catch (error: any) {
      console.error('Error toggling prompt:', error);
      alert(error.response?.data?.error || 'Failed to update prompt. Please try again.');
    }
  };

  const openEdit = (prompt: SystemPrompt) => {
    setSelectedPrompt(prompt);
    setModalOpen(true);
  };

  const getScopeIcon = (scope: string) => {
    switch (scope) {
      case 'global': return <Globe size={16} className="text-blue-600" />;
      case 'network': return <Building2 size={16} className="text-green-600" />;
      case 'site': return <MapPin size={16} className="text-orange-600" />;
      default: return <MessageSquare size={16} className="text-gray-600" />;
    }
  };

  const getScopeLabel = (scope: string) => {
    switch (scope) {
      case 'global': return 'Global Prompts';
      case 'network': return 'Network Prompts';
      case 'site': return 'Site Prompts';
      default: return 'Prompts';
    }
  };

  const canManagePrompt = (prompt: SystemPrompt) => {
    if (isSuperAdmin) return true;
    if (prompt.scope === 'global') return false;
    if (!isAdmin) return false;
    return true;
  };

  const filteredPrompts = prompts.filter(p =>
    filter === 'all' || p.scope === filter
  );

  const groupedPrompts = filteredPrompts.reduce((acc, p) => {
    if (!acc[p.scope]) acc[p.scope] = [];
    acc[p.scope].push(p);
    return acc;
  }, {} as Record<string, SystemPrompt[]>);

  const scopeOrder: Scope[] = ['global', 'network', 'site'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  // Non-admin users see placeholder settings
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto">
        <PageHeader
          className="mb-6"
          title="Settings"
          icon={SettingsIcon}
          subtitle="Configure your Site Intelligence preferences"
        />

        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <SettingsIcon size={48} className="mx-auto text-gray-300 mb-4 dark:text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">Settings coming soon</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configuration options for notifications, default protocol, and more will be available here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        className="mb-6"
        title="Settings"
        icon={SettingsIcon}
        subtitle="Configure Site Intelligence preferences and AI behavior"
      />

      {/* System Prompts Section - Admin only */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">System Prompts</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Configure AI instructions and guidelines</p>
          </div>
          <button
            onClick={() => {
              setSelectedPrompt(null);
              setModalOpen(true);
            }}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            <Plus size={16} />
            Add Prompt
          </button>
        </div>

        {/* Info banner */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4 dark:bg-primary-900/20 dark:border-primary-800">
          <p className="text-sm text-primary-800 dark:text-primary-300">
            <strong>System prompts</strong> are instructions given to the AI before it answers questions.
            Use them to set guidelines or customize AI behavior for your organization.
          </p>
        </div>

        {/* Base Prompt (Super Admin only) */}
        {isSuperAdmin && basePrompt && (
          <div className="mb-4">
            <div className="bg-white rounded-xl border border-primary-200 p-4 dark:bg-gray-800 dark:border-primary-800">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <MessageSquare size={16} className="text-primary-600 dark:text-primary-400" />
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">{basePrompt.name}</h3>
                    <span className="text-xs font-medium text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full dark:bg-primary-900/40 dark:text-primary-300">
                      Base Prompt
                    </span>
                    <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full dark:bg-gray-700 dark:text-gray-300">
                      Read Only
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">{basePrompt.description}</p>
                  <button
                    onClick={() => setBasePromptExpanded(!basePromptExpanded)}
                    className="flex items-center gap-1 text-sm font-medium text-primary-600 transition-colors duration-150 hover:text-primary-700 mt-2 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    {basePromptExpanded ? (
                      <>
                        <ChevronUp size={16} />
                        Hide prompt text
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} />
                        Show prompt text ({basePrompt.prompt_text.length.toLocaleString()} chars)
                      </>
                    )}
                  </button>
                  {basePromptExpanded && (
                    <pre className="mt-3 p-3 bg-gray-50 rounded-lg text-xs text-gray-700 font-mono whitespace-pre-wrap max-h-64 overflow-y-auto border border-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700">
                      {basePrompt.prompt_text}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {(['all', 'global', 'network', 'site'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={chipCls(filter === f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Prompt list */}
        {filteredPrompts.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center dark:bg-gray-800 dark:border-gray-700">
            <MessageSquare size={32} className="mx-auto text-gray-300 mb-2 dark:text-gray-600" />
            <p className="text-gray-500 text-sm dark:text-gray-400">No system prompts yet. Add prompts to customize AI behavior.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {scopeOrder.map((scope) => {
              const scopePrompts = groupedPrompts[scope] || [];
              if (scopePrompts.length === 0) return null;

              return (
                <div key={scope}>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2 dark:text-gray-400">
                    {getScopeIcon(scope)}
                    {getScopeLabel(scope)}
                    <span className="text-gray-400 font-normal dark:text-gray-500">({scopePrompts.length})</span>
                  </h3>
                  <div className="space-y-2">
                    {scopePrompts.map((prompt) => (
                      <div
                        key={prompt.id}
                        className={`bg-white rounded-lg border p-3 dark:bg-gray-800 ${
                          prompt.is_active ? 'border-gray-200 dark:border-gray-700' : 'border-gray-200 bg-gray-50 opacity-60 dark:border-gray-700 dark:bg-gray-800/50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-medium text-gray-900 text-sm dark:text-gray-100">{prompt.name}</h4>
                              <span className="text-xs font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-full dark:bg-gray-700 dark:text-gray-300">
                                Priority: {prompt.priority}
                              </span>
                              {!prompt.is_active && (
                                <span className="text-xs font-medium text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded-full dark:bg-gray-700 dark:text-gray-300">
                                  Inactive
                                </span>
                              )}
                            </div>
                            {prompt.description && (
                              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">{prompt.description}</p>
                            )}
                            <pre className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700 font-mono whitespace-pre-wrap max-h-24 overflow-y-auto dark:bg-gray-900 dark:text-gray-300">
                              {prompt.prompt_text}
                            </pre>
                          </div>
                          {canManagePrompt(prompt) && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleToggleActive(prompt)}
                                className={`p-1 rounded ${
                                  prompt.is_active
                                    ? 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20'
                                    : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                                title={prompt.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {prompt.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                              </button>
                              <button
                                onClick={() => openEdit(prompt)}
                                className="p-1 text-gray-400 transition-colors duration-150 hover:text-primary-600 rounded hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20"
                                title="Edit"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => handleDelete(prompt.id)}
                                className="p-1 text-gray-400 transition-colors duration-150 hover:text-red-600 rounded hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Future settings placeholder */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center dark:bg-gray-800 dark:border-gray-700">
        <SettingsIcon size={32} className="mx-auto text-gray-300 mb-2 dark:text-gray-600" />
        <h3 className="text-sm font-medium text-gray-900 mb-1 dark:text-gray-100">More settings coming soon</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Notifications, default protocol, and other preferences will be available here.
        </p>
      </div>

      {/* Modal */}
      <PromptModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedPrompt(null);
        }}
        onSave={handleSave}
        prompt={selectedPrompt}
        userRole={userRole}
        currentSiteId={currentSite?.id || null}
      />
    </div>
  );
}
